import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import EditMaintenanceDialog from './EditMaintenanceDialog';
import EditWorkOrderDialog from './EditWorkOrderDialog';
import YearHeatmap from './YearHeatmap';
import MaintenanceCalendar from './MaintenanceCalendar';
import LogSummaryPopover from './LogSummaryPopover';
import { isoDay } from '../lib/historyMeta';
import { HistoryList } from './history/HistoryList';
import { DeleteRequestConfirm } from './DeleteRequestConfirm';

/**
 * Shared maintenance-history view used by both the plant-wide page and the
 * per-location page. Toggle between:
 *   - List view   → /api/maintenance-history (merged completed feed)
 *   - Calendar view → /api/calendar-events (all 4 event types, by day) + Heatmap on top
 *
 * Props:
 *   - locationId?: string   (scope to one plant)
 *   - user: { role, ... }
 *   - title: string         (heading text)
 *   - subtitle?: string
 */
const MaintenanceHistoryView = ({ locationId, user, title, subtitle }) => {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'technician';

  const [view, setView] = useState('list'); // 'list' | 'calendar'
  const [history, setHistory] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const historyRequest = useRef(0);
  const [loadingCalendar, setLoadingCalendar] = useState(true);
  const [search, setSearch] = useState('');
  const [calendarSearch, setCalendarSearch] = useState('');
  const [editingLog, setEditingLog] = useState(null);
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);
  const [deletingLogId, setDeletingLogId] = useState(null);
  const [deletingWorkOrderId, setDeletingWorkOrderId] = useState(null);
  const [deletingRequestId, setDeletingRequestId] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(null); // 'YYYY-MM'
  // Time-range window for the List view. '90' is the default so a normal
  // maintenance quarter always loads instantly; users can pull older
  // records by widening the window or clicking "Search all history" when
  // typing in the search box.
  const [range, setRange] = useState('90'); // '30' | '90' | '365' | 'all'

  const rangeToFromDate = (r) => {
    if (r === 'all') return null;
    const days = parseInt(r, 10);
    if (!Number.isFinite(days) || days <= 0) return null;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  };

  const buildQS = (params) => {
    const qs = Object.entries(params)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    return qs ? `?${qs}` : '';
  };

  const fetchHistory = async () => {
    const request = ++historyRequest.current;
    setLoadingHistory(true);
    setHistoryError('');
    try {
      const res = await axios.get(`${API}/maintenance-history${buildQS({
        location_id: locationId,
        from_date: rangeToFromDate(range),
      })}`);
      if (request === historyRequest.current) setHistory(res.data);
    } catch (e) {
      if (request === historyRequest.current) {
        setHistory([]);
        setHistoryError('Maintenance history could not be loaded. Please retry.');
      }
    } finally {
      if (request === historyRequest.current) setLoadingHistory(false);
    }
  };

  const fetchCalendar = async () => {
    setLoadingCalendar(true);
    try {
      const res = await axios.get(`${API}/calendar-events${buildQS({ location_id: locationId })}`);
      setCalendarEvents(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load calendar events');
    } finally {
      setLoadingCalendar(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchCalendar();
    // Re-fetch history when the range chip changes so we don't over-fetch
    // everything on first load.
  }, [locationId, range]);

  useEffect(() => () => { historyRequest.current += 1; }, []);
  useEffect(() => {
    const refresh = () => { fetchHistory(); fetchCalendar(); };
    window.addEventListener('maintenance-records-changed', refresh);
    return () => window.removeEventListener('maintenance-records-changed', refresh);
  }, [locationId, range]);

  const handleDeleteLog = async (logId) => {
    try {
      await axios.delete(`${API}/maintenance-logs/${logId}`);
      toast.success('Maintenance log deleted');
      setDeletingLogId(null);
      fetchHistory();
      fetchCalendar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete log');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/maintenance-requests/${requestId}`);
      toast.success('Maintenance request deleted');
      setDeletingRequestId(null);
      fetchHistory();
      fetchCalendar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete maintenance request');
    }
  };

  const handleDeleteWorkOrder = async (workOrderId) => {
    try {
      await axios.delete(`${API}/work-orders/${workOrderId}`);
      toast.success('Work order deleted');
      setDeletingWorkOrderId(null);
      fetchHistory();
      fetchCalendar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete work order');
    }
  };

  // Fetch the FULL maintenance log by its raw UUID before opening the edit
  // dialog. The history feed payload only carries display fields (title,
  // description) and a synthetic composite id like "log:<uuid>". The edit
  // dialog needs the real log record (work_description, hours_spent,
  // work_date, notes, images, ...) with the real UUID for the PUT to work.
  const openEditDialog = async (rawId) => {
    try {
      const res = await axios.get(`${API}/maintenance-logs/${rawId}`);
      setEditingLog(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load maintenance log');
    }
  };

  // Same pattern for completed work orders — fetch the canonical record
  // (including hours_spent/parts_used/completed_at) so the edit dialog can
  // pre-populate the completion block.
  const openEditWorkOrderDialog = async (rawId) => {
    try {
      const res = await axios.get(`${API}/work-orders/${rawId}`);
      setEditingWorkOrder(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load work order');
    }
  };

  // ---- List view filtering
  const filteredHistory = useMemo(() => {
    if (!search.trim()) return history;
    const needle = search.trim().toLowerCase();
    return history.filter((e) =>
      [
        e.equipment_name,
        e.title,
        e.description,
        e.resolution,
        e.notes,
        e.location_name,
        e.wo_number,
        ...(e.follow_ups || []).map((f) => f.description),
        ...(e.findings || []).map((f) => `${f.description} ${f.action_notes || ''}`),
        e.parts_used,
        ...(e.serialized_work || []).map((r) => `${r.serial_number} ${r.mfr_part_number} ${r.from_location} ${r.to_location}`),
        e.actor_name,
      ]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle))
    );
  }, [history, search]);

  // Stats for the heatmap header
  const filteredCalendarEvents = useMemo(() => {
    if (!calendarSearch.trim()) return calendarEvents;
    const needle = calendarSearch.toLowerCase();
    return calendarEvents.filter((e) =>
      [
        e.equipment_name,
        e.title,
        e.description,
        e.resolution,
        e.parts_used,
        ...(e.serialized_work || []).map((r) => `${r.serial_number} ${r.mfr_part_number} ${r.from_location} ${r.to_location}`),
        e.notes,
        e.actor_name,
      ]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle))
    );
  }, [calendarEvents, calendarSearch]);

  const eventCount = filteredCalendarEvents.length;
  const dateBounds = useMemo(() => {
    if (filteredCalendarEvents.length === 0) return null;
    const sorted = [...filteredCalendarEvents]
      .map((e) => e.date)
      .filter(Boolean)
      .sort();
    return { oldest: sorted[0], newest: sorted[sorted.length - 1] };
  }, [filteredCalendarEvents]);

  const [selectedLog, setSelectedLog] = useState(null);

  const onEventClick = (event) => {
    // Level-A calendar upgrade: read the log inline instead of navigating away.
    // Maintenance requests + work orders still deep-link to the equipment page
    // because they don't have a rich body worth popping over.
    if (event.event_type === 'maintenance_log') {
      // Fetch the full log so we get images + notes that the calendar-events
      // endpoint doesn't stream by default.
      (async () => {
        try {
          const res = await axios.get(`${API}/maintenance-logs/${event.raw_id}`);
          setSelectedLog({
            ...event,
            description: res.data.work_description || event.description,
            resolution: res.data.resolution || event.resolution,
            parts_used: res.data.parts_used || event.parts_used,
            notes: res.data.notes || event.notes,
            hours_spent: res.data.hours_spent,
            actor_name: res.data.technician_name || event.actor_name,
            images: res.data.images || [],
          });
        } catch {
          // Fall back to whatever calendar-events already gave us.
          setSelectedLog(event);
        }
      })();
      return;
    }
    if (event.machinery_id) {
      navigate(`/machinery/${event.machinery_id}`);
    }
  };

  const onHeatmapClick = (day) => {
    // Jump calendar to that month, and select the day.
    if (!day) return;
    setView('calendar');
    setCalendarMonth(day.slice(0, 7));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="history-page-title">{title}</h1>
          {subtitle && (
            <p className="text-sm text-slate-600 mt-1" data-testid="history-page-subtitle">{subtitle}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setView('list')}
              data-testid="history-view-list-btn"
              aria-pressed={view === 'list'}
              className={`px-3 py-1.5 text-sm rounded ${
                view === 'list' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              List
            </button>
            <button
              type="button"
              onClick={() => setView('calendar')}
              data-testid="history-view-calendar-btn"
              aria-pressed={view === 'calendar'}
              className={`px-3 py-1.5 text-sm rounded ${
                view === 'calendar' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              Calendar
            </button>
          </div>
        </div>
      </div>

      {view === 'list' && (
        <HistoryList events={filteredHistory} totalLoaded={history.length}
          search={search} setSearch={setSearch} range={range} setRange={setRange}
          loading={loadingHistory} error={historyError} onRetry={fetchHistory}
          isAdmin={isAdmin} onEditLog={openEditDialog} onEditWorkOrder={openEditWorkOrderDialog}
          onDeleteLog={setDeletingLogId} onDeleteWorkOrder={setDeletingWorkOrderId} onDeleteRequest={setDeletingRequestId} />
      )}

      {view === 'calendar' && (
        <>
          {/* Search bar for calendar / heatmap */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[240px]">
                <Input
                  data-testid="calendar-search-input"
                  placeholder="Search by equipment, title, description, resolution, parts, technician…"
                  value={calendarSearch}
                  onChange={(e) => setCalendarSearch(e.target.value)}
                />
              </div>
              {calendarSearch.trim() && (
                <>
                  <div className="text-xs text-slate-600" data-testid="calendar-search-count">
                    {eventCount} event{eventCount === 1 ? '' : 's'} match
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCalendarSearch('')}
                    data-testid="calendar-search-clear-btn"
                  >
                    Clear
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Year heatmap */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-end justify-between mb-2">
              <div>
                <div className="text-sm font-semibold">Activity over the last year</div>
                <div className="text-xs text-slate-500">
                  {loadingCalendar
                    ? 'Loading…'
                    : `${eventCount} event${eventCount === 1 ? '' : 's'}${
                        dateBounds ? ` · oldest ${dateBounds.oldest} · newest ${dateBounds.newest}` : ''
                      }`}
                </div>
              </div>
            </div>
            <YearHeatmap
              events={filteredCalendarEvents}
              onDayClick={onHeatmapClick}
              endDate={new Date()}
            />
          </div>

          {/* Calendar with side panel */}
          <div className="bg-white rounded-lg shadow p-4">
            {loadingCalendar ? (
              <div className="text-center text-slate-600 py-8">Loading…</div>
            ) : (
              <MaintenanceCalendar
                events={filteredCalendarEvents}
                onEventClick={onEventClick}
                initialMonth={calendarMonth || isoDay(new Date()).slice(0, 7)}
              />
            )}
          </div>
        </>
      )}

      {editingLog && (
        <EditMaintenanceDialog
          open={!!editingLog}
          log={editingLog}
          onClose={() => setEditingLog(null)}
          onSuccess={() => {
            setEditingLog(null);
            fetchHistory();
            fetchCalendar();
          }}
        />
      )}

      {editingWorkOrder && (
        <EditWorkOrderDialog
          open={!!editingWorkOrder}
          workOrder={editingWorkOrder}
          onClose={() => setEditingWorkOrder(null)}
          onSuccess={() => {
            setEditingWorkOrder(null);
            fetchHistory();
            fetchCalendar();
          }}
        />
      )}

      <LogSummaryPopover
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        event={selectedLog}
      />

      {deletingLogId && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setDeletingLogId(null)}
          data-testid="delete-log-overlay"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Delete this maintenance log?
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              This permanently removes the log entry. Inventory deductions already applied
              from this log will <span className="font-medium">not</span> be refunded — adjust stock manually
              if needed.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeletingLogId(null)} data-testid="cancel-delete-log-btn">
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleDeleteLog(deletingLogId)}
                data-testid="confirm-delete-log-btn"
              >
                Delete log
              </Button>
            </div>
          </div>
        </div>
      )}

      {deletingWorkOrderId && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setDeletingWorkOrderId(null)}
          data-testid="delete-wo-overlay"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Delete this work order?
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              This permanently removes the work order and all its completion details from
              the history. Inventory or parts already deducted at completion time will{' '}
              <span className="font-medium">not</span> be refunded — adjust stock manually
              if needed.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeletingWorkOrderId(null)} data-testid="cancel-delete-wo-btn">
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleDeleteWorkOrder(deletingWorkOrderId)}
                data-testid="confirm-delete-wo-btn"
              >
                Delete work order
              </Button>
            </div>
          </div>
        </div>
      )}
      {deletingRequestId && (
        <DeleteRequestConfirm onCancel={() => setDeletingRequestId(null)}
          onConfirm={() => handleDeleteRequest(deletingRequestId)} />
      )}
    </div>
  );
};

export default MaintenanceHistoryView;
